/**
 * Usage statistics on Agents › All agents (#163): the row keeps the account
 * area's anatomy — value and lock side by side — and under it three lines say
 * which of the three answers is chosen. Collected and locked is every member;
 * off and locked is nobody; unlocked, each member chooses. Locked, an amber line
 * says what changes on the machines at their next check.
 *
 * Collection is `onboarding.usageStatsEnabled === true` (the Worker's
 * `collectsUsage`), so an unanswered row reads Not collected whatever the app's
 * own default is.
 */
import type React from 'react';
import { color } from '../../theme/tokens';
import { LockMark } from '../../components/icons';

export const USAGE_KEY = 'usageStatsEnabled';

export interface UsageRowState {
  /** The organization has written a value. */
  answered: boolean;
  /** That value, read as collection: true only when it is true. */
  on: boolean;
  locked: boolean;
}

/** The value as the row names it. */
export const usageWord = (on: boolean): string => (on ? 'Collected' : 'Not collected');

export function usageOutcome({ answered, on, locked }: UsageRowState, org: string): { outcome: string; how: string } {
  if (!answered) {
    return {
      outcome: 'Not collected — each member chooses.',
      how: 'Each member’s app keeps its own, on their machine, as they choose; nothing is sent here. This is Airlock’s default.'
    };
  }
  if (on && locked) {
    return {
      outcome: 'Collected from every member.',
      how: `Every app keeps statistics and sends a row a day to ${org}; members cannot turn them off.`
    };
  }
  if (on) {
    return {
      outcome: 'Collected — each member chooses.',
      how: `Apps that keep statistics send a row a day to ${org}. A member who turns theirs off in App settings sends nothing.`
    };
  }
  if (locked) {
    return { outcome: 'Off for every member.', how: 'No app measures anything, and nothing is sent. Members cannot turn statistics on.' };
  }
  return {
    outcome: 'Not collected — each member chooses.',
    how: 'Each member’s app keeps its own, on their machine, as they choose; nothing is sent here.'
  };
}

/** The three lines under the row. */
export function UsageHint({ state, org }: { state: UsageRowState; org: string }): React.JSX.Element {
  const { outcome, how } = usageOutcome(state, org);
  const warn = usageWarn(state);
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2, maxWidth: 600 }}>
      <span key={outcome} style={{ fontWeight: 600, color: color.ink, animation: 'usageRowIn .14s ease' }}>
        {outcome}
      </span>
      <span style={{ color: color.inkMuted }}>{how}</span>
      <span style={{ color: color.inkSubtle }}>
        A row a day per member: turns, tokens, cost and what landed, per model and effort — never a prompt, a file or a
        repository name. Administrators read everyone’s; each member reads their own.
      </span>
      {warn ? (
        <span style={{ marginTop: 2, display: 'flex', gap: 8, color: color.warnText, animation: 'usageRowIn .14s ease' }}>
          <span aria-hidden="true" style={{ flex: 'none', marginTop: 2, display: 'flex' }}>
            <LockMark size={12} />
          </span>
          <span>{warn}</span>
        </span>
      ) : null}
      <style>{'@keyframes usageRowIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }'}</style>
    </span>
  );
}

/** Locked: what the machines do at their next check. */
export function usageWarn({ answered, on, locked }: UsageRowState): string | null {
  if (!answered || !locked) return null;
  return on
    ? 'Locked on: a member who had turned statistics off starts counting and sending at their next check.'
    : 'Locked off: members’ apps stop counting at their next check. What each kept stays on their machine.';
}

/** The tag a row the organization has not answered wears beside its control. */
export function AirlockDefault(): React.JSX.Element {
  return (
    <span
      style={{
        height: 17,
        padding: '0 5px',
        display: 'inline-flex',
        alignItems: 'center',
        border: `1px solid ${color.line}`,
        borderRadius: 4,
        background: color.surfaceAlt,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9.5,
        letterSpacing: '.06em',
        color: color.inkSubtle,
        whiteSpace: 'nowrap'
      }}
    >
      AIRLOCK DEFAULT
    </span>
  );
}
